import { Injectable } from '@angular/core';

const ENCRYPTION_KEY_NAME = 'gars_storage_key';
const HMAC_KEY_NAME = 'gars_hmac_key';
const KEY_REGISTRY_NAME = 'gars_key_registry';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private aesKey: CryptoKey | null = null;
  private hmacKey: CryptoKey | null = null;

  constructor() {}

  async set<T>(key: string, value: T, encrypt = true): Promise<void> {
    if (encrypt) {
      const encKey = await this.encryptKey(key);
      const encrypted = await this.encrypt(JSON.stringify(value));
      localStorage.setItem(encKey, encrypted);
      await this.registerKey(key, encKey);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }

  async get<T>(key: string, encrypt = true): Promise<T | null> {
    if (encrypt) {
      const encKey = await this.encryptKey(key);
      const raw = localStorage.getItem(encKey);
      if (!raw) return null;
      try {
        const decrypted = await this.decrypt(raw);
        return JSON.parse(decrypted) as T;
      } catch {
        return null;
      }
    }
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  async delete(key: string, encrypt = true): Promise<void> {
    if (encrypt) {
      const encKey = await this.encryptKey(key);
      localStorage.removeItem(encKey);
      await this.unregisterKey(encKey);
    } else {
      localStorage.removeItem(key);
    }
  }

  clear(): void {
    localStorage.clear();
  }

  async has(key: string, encrypt = true): Promise<boolean> {
    if (encrypt) {
      const encKey = await this.encryptKey(key);
      return localStorage.getItem(encKey) !== null;
    }
    return localStorage.getItem(key) !== null;
  }

  async keys(): Promise<string[]> {
    const registry = await this.getKeyRegistry();
    return Object.keys(registry);
  }

  private async encryptKey(key: string): Promise<string> {
    const hmac = await this.getOrCreateHmacKey();
    const encoded = new TextEncoder().encode(key);
    const signature = await crypto.subtle.sign('HMAC', hmac, encoded);
    return this.bufferToBase64(new Uint8Array(signature));
  }

  private async encrypt(plainText: string): Promise<string> {
    const key = await this.getOrCreateAesKey();
    const iv = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(12)));
    const encoded = new TextEncoder().encode(plainText);
    const cipherBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoded,
    );
    const combined = new Uint8Array(new ArrayBuffer(iv.length + cipherBuffer.byteLength));
    combined.set(iv);
    combined.set(new Uint8Array(cipherBuffer), iv.length);
    return this.bufferToBase64(combined);
  }

  private async decrypt(cipherText: string): Promise<string> {
    const key = await this.getOrCreateAesKey();
    const { iv, data } = this.base64ToBuffer(cipherText);
    const plainBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data,
    );
    return new TextDecoder().decode(plainBuffer);
  }

  private async getOrCreateAesKey(): Promise<CryptoKey> {
    if (this.aesKey) return this.aesKey;

    const stored = localStorage.getItem(ENCRYPTION_KEY_NAME);
    if (stored) {
      const rawKey = this.base64ToRawKey(stored);
      this.aesKey = await crypto.subtle.importKey(
        'raw',
        rawKey,
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt'],
      );
      return this.aesKey;
    }

    this.aesKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt'],
    );
    const exported = await crypto.subtle.exportKey('raw', this.aesKey);
    localStorage.setItem(ENCRYPTION_KEY_NAME, this.base64EncodeRaw(exported));
    return this.aesKey;
  }

  private async getOrCreateHmacKey(): Promise<CryptoKey> {
    if (this.hmacKey) return this.hmacKey;

    const stored = localStorage.getItem(HMAC_KEY_NAME);
    if (stored) {
      const rawKey = this.base64ToRawKey(stored);
      this.hmacKey = await crypto.subtle.importKey(
        'raw',
        rawKey,
        { name: 'HMAC', hash: 'SHA-256' },
        true,
        ['sign'],
      );
      return this.hmacKey;
    }

    this.hmacKey = await crypto.subtle.generateKey(
      { name: 'HMAC', hash: 'SHA-256' },
      true,
      ['sign'],
    );
    const exported = await crypto.subtle.exportKey('raw', this.hmacKey);
    localStorage.setItem(HMAC_KEY_NAME, this.base64EncodeRaw(exported));
    return this.hmacKey;
  }

  private async getKeyRegistry(): Promise<Record<string, string>> {
    const raw = localStorage.getItem(KEY_REGISTRY_NAME);
    if (!raw) return {};
    try {
      const decrypted = await this.decrypt(raw);
      return JSON.parse(decrypted);
    } catch {
      return {};
    }
  }

  private async saveKeyRegistry(registry: Record<string, string>): Promise<void> {
    const encrypted = await this.encrypt(JSON.stringify(registry));
    localStorage.setItem(KEY_REGISTRY_NAME, encrypted);
  }

  private async registerKey(plainKey: string, encKey: string): Promise<void> {
    const registry = await this.getKeyRegistry();
    registry[plainKey] = encKey;
    await this.saveKeyRegistry(registry);
  }

  private async unregisterKey(encKey: string): Promise<void> {
    const registry = await this.getKeyRegistry();
    const plainKey = Object.keys(registry).find((k) => registry[k] === encKey);
    if (plainKey) {
      delete registry[plainKey];
      await this.saveKeyRegistry(registry);
    }
  }

  private bufferToBase64(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary);
  }

  private base64ToBuffer(base64: string): { iv: Uint8Array<ArrayBuffer>; data: Uint8Array<ArrayBuffer> } {
    const binary = atob(base64);
    const buf = new ArrayBuffer(binary.length);
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return {
      iv: new Uint8Array(buf, 0, 12),
      data: new Uint8Array(buf, 12),
    };
  }

  private base64EncodeRaw(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary);
  }

  private base64ToRawKey(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const buf = new ArrayBuffer(binary.length);
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return buf;
  }
}
